const preloadedChunks = new Set();
let hoverPrefetchCleanup = null;

const chunkLoaders = {
  store: () => import('../components/Store'),
  seriesDetail: () => import('../components/GradedReaders/Detail/SeriesDetail'),
  unifiedReader: () => import('../components/GradedReaders/Reader/UnifiedReader'),
  libraryHome: () => import('../components/library/LibraryHome'),
  collectionDetail: () => import('../components/library/CollectionDetail'),
  podcastLessons: () => import('../components/PodcastLessons'),
  episodePlayer: () => import('../components/EpisodePlayerPage'),
  dashboard: () => import('../components/Dashboard'),
  coach: () => import('../components/Coach'),
  articleDetail: () => import('../components/language-lab/ArticleDetail'),
  podcastDetail: () => import('../components/language-lab/PodcastDetail'),
  mixedDetail: () => import('../components/language-lab/MixedMediaDetail'),
  adaptiveFlow: () => import('../components/ExtraPracticeModule/AdaptiveFlow'),
  extraPracticeExercise: () => import('../components/ExtraPracticeModule/ExtraPracticeExercisePage'),
};

const PREFETCH_RULES = [
  {
    test: (path) => path === '/' || path.startsWith('/podcast-lessons'),
    chunks: ['podcastLessons', 'store', 'libraryHome', 'coach'],
  },
  {
    test: (path) => path.startsWith('/store'),
    chunks: ['store', 'seriesDetail', 'unifiedReader', 'libraryHome'],
  },
  {
    test: (path) => path.startsWith('/library'),
    chunks: ['libraryHome', 'collectionDetail', 'unifiedReader', 'store'],
  },
  {
    test: (path) => path.startsWith('/podcast-lessons'),
    chunks: ['podcastLessons', 'episodePlayer', 'dashboard', 'coach'],
  },
  {
    test: (path) => path.startsWith('/lessons/'),
    chunks: ['episodePlayer', 'dashboard', 'coach'],
  },
  {
    test: (path) => path.startsWith('/dashboard'),
    chunks: ['dashboard', 'coach', 'podcastLessons', 'adaptiveFlow'],
  },
  {
    test: (path) => path.startsWith('/coach') || path.startsWith('/language-lab') || path.startsWith('/language-hub'),
    chunks: ['coach', 'articleDetail', 'podcastDetail', 'mixedDetail', 'adaptiveFlow', 'extraPracticeExercise'],
  },
];

function normalizePath(pathname) {
  if (typeof pathname !== 'string' || pathname.length === 0) return '/';
  const stripped = pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '');
  return stripped || '/';
}

function preloadChunk(name) {
  if (!name || preloadedChunks.has(name)) return;
  const loader = chunkLoaders[name];
  if (typeof loader !== 'function') return;

  preloadedChunks.add(name);
  Promise.resolve()
    .then(loader)
    .catch(() => {
      preloadedChunks.delete(name);
    });
}

export function prefetchRouteBundles(pathname) {
  const normalizedPath = normalizePath(pathname);
  const seen = new Set();

  PREFETCH_RULES.forEach((rule) => {
    if (!rule.test(normalizedPath)) return;
    rule.chunks.forEach((chunkName) => {
      if (seen.has(chunkName)) return;
      seen.add(chunkName);
      preloadChunk(chunkName);
    });
  });
}

export function prefetchDefaultBundles() {
  ['podcastLessons', 'store', 'libraryHome', 'dashboard', 'coach'].forEach(preloadChunk);
}

export function installHoverPrefetch() {
  if (hoverPrefetchCleanup || typeof document === 'undefined' || typeof window === 'undefined') {
    return hoverPrefetchCleanup || (() => {});
  }

  const onPointerOver = (event) => {
    const target = event.target;
    if (!target?.closest) return;
    const anchor = target.closest('a[href]');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      prefetchRouteBundles(url.pathname);
    } catch {
      // Ignore invalid URLs.
    }
  };

  document.addEventListener('pointerover', onPointerOver, { passive: true });
  hoverPrefetchCleanup = () => {
    document.removeEventListener('pointerover', onPointerOver);
    hoverPrefetchCleanup = null;
  };

  return hoverPrefetchCleanup;
}
